/**
 * Role-privilege audit for the /admin Access Health tab: compare each Vera
 * security role's actually-attached privileges against the shared access
 * matrix (the same source of truth the provisioning script writes from).
 *
 * Detects the SECOND access failure mode (2026-07-02): a user on the right
 * team whose ROLE is missing a privilege the app needs (e.g. AppendTo on a
 * lookup-target table). Report-only by design; fixing role definitions stays
 * a developer action (schema:provision-vera-security-roles), because handing
 * the UI privilege-grant rights would be far more power than membership
 * repair.
 *
 * Requires prvReadRole on the caller (granted to Vera Admin). The privilege
 * catalog itself is org-readable.
 */

import { dataverseClient } from '@shared/api';
import type { TableName } from '@shared/api/dataverse/types';
import { VERA_DATAVERSE_ROLES, privilegesNeededForRole } from '@shared/auth';

const ROLES_SET = 'roles' as TableName;
const PRIVILEGES_SET = 'privileges' as TableName;

export interface RolePrivilegeGap {
  roleName: string;
  /** False when the role doesn't exist in this environment at all. */
  roleFound: boolean;
  /** Expected privilege names (per the matrix) not attached to the role. */
  missing: string[];
  /** Expected names that don't exist in this environment's privilege catalog
   *  (table not provisioned here) — informational, not fixable by the role
   *  provisioning script alone. */
  notInEnvironment: string[];
  expectedCount: number;
}

export interface RolePrivilegeAuditResult {
  gaps: RolePrivilegeGap[];
  /** True when every found role carries every expected privilege. */
  healthy: boolean;
}

interface RoleRow {
  roleid: string;
  name?: string;
}
interface PrivilegeRow {
  privilegeid: string;
  name?: string;
}
interface RolePrivilegesResponse {
  RolePrivileges?: Array<{ PrivilegeId: string }>;
}

export async function auditRolePrivileges(): Promise<RolePrivilegeAuditResult> {
  // Privilege catalog: id -> lowercased name (and the reverse set for
  // existence checks). One paged pull, ~10k rows of two columns.
  const catalog = await dataverseClient.listAll<PrivilegeRow>(PRIVILEGES_SET, {
    select: ['privilegeid', 'name'],
  });
  const nameById = new Map<string, string>();
  const knownNames = new Set<string>();
  for (const p of catalog) {
    if (!p.name) continue;
    nameById.set(p.privilegeid.toLowerCase(), p.name.toLowerCase());
    knownNames.add(p.name.toLowerCase());
  }

  const gaps: RolePrivilegeGap[] = [];
  for (const role of VERA_DATAVERSE_ROLES) {
    const expected = privilegesNeededForRole(role.key);

    const rows = await dataverseClient.list<RoleRow>(ROLES_SET, {
      filter: `name eq '${role.name.replace(/'/g, "''")}'`,
      select: ['roleid', 'name'],
      top: 1,
    });
    const roleRow = rows.value[0];
    if (!roleRow) {
      gaps.push({
        roleName: role.name,
        roleFound: false,
        missing: [],
        notInEnvironment: [],
        expectedCount: expected.length,
      });
      continue;
    }

    // RetrieveRolePrivilegesRole is an OData function; get() renders
    // `<name>(<id>)` which matches its `(RoleId=...)` calling convention.
    const rp = await dataverseClient.get<RolePrivilegesResponse>(
      'RetrieveRolePrivilegesRole' as TableName,
      `RoleId=${roleRow.roleid}`,
    );
    const attached = new Set(
      (rp.RolePrivileges ?? [])
        .map((p) => nameById.get(p.PrivilegeId.toLowerCase()))
        .filter(Boolean) as string[],
    );

    const missing: string[] = [];
    const notInEnvironment: string[] = [];
    for (const name of expected) {
      const lower = name.toLowerCase();
      if (!knownNames.has(lower)) notInEnvironment.push(name);
      else if (!attached.has(lower)) missing.push(name);
    }
    gaps.push({
      roleName: role.name,
      roleFound: true,
      missing,
      notInEnvironment,
      expectedCount: expected.length,
    });
  }

  return {
    gaps,
    healthy: gaps.every((g) => g.roleFound && g.missing.length === 0),
  };
}
